import { Email, Textsms } from "@mui/icons-material";
import { Badge, Box, Tab, Tabs } from "@mui/material";
import dayjs from "dayjs";
import { memo, useMemo, useRef, useState } from "react";
import * as TinyMCETypes from "tinymce";
import { useMessageDialogHandlers } from "../../../../App/State/Hooks/MessageDialog/useMessageDialog";
import {
  useMessageInputHandlers,
  useMessageInputState,
} from "../../../../App/State/Hooks/MessageDialog/useMessageInput";
import {
  useMessageScheduleHandlers,
  useMessageScheduleState,
} from "../../../../App/State/Hooks/MessageDialog/useMessageSchedule";
import { useMessageTemplateHandlers } from "../../../../App/State/Hooks/MessageDialog/useMessageTemplate";
import { useAppSelector } from "../../../../App/State/Hooks/reduxHooks";
import { selectSelectedCandidates } from "../../../../App/State/Slices/MessageDialogSlice";
import { useUserProfileStore } from "../../../../App/State/Stores/UserProfileStore";
import { IMessageSendPost } from "../../../../App/Typings/MessageText";
import { toNearestFiveMinutes } from "../../../../MessagingCenter/Utils";
import { IMessageContainers } from "../../../Enums/MessageEnums";
import { GetContainerTypeByTabNumber } from "../../../FunctionMethods/MessageFunctions";
import EmailMessageSendOrSchedule from "../../../MessageDialog/Components/EmailMessageSendOrSchedule";
import TextMessageSendOrSchedule from "../../../MessageDialog/Components/TextMessageSendOrSchedule";
import useMessageMutationState from "../../../MessageDialog/State/useMessageMutationState";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function historyListProps(index: number) {
  return {
    id: `group-message-tab-${index}`,
    "aria-controls": `group-message-tabpanel-${index}`,
  };
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      sx={{ visibility: value !== index ? "hidden" : "visible", height: value !== index ? 0 : "inherit" }}
      id={`group-message-tabpanel-${index}`}
      aria-labelledby={`group-message-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </Box>
  );
}

const MemoTabPanel = memo(TabPanel);

const getMessageType = (containerType: IMessageContainers) => {
  if (IMessageContainers[containerType].includes("Email")) {
    return "email";
  } else {
    return "text";
  }
};

export default function MessageDeliveryTabs({
  groupId,
  campaignId,
}: {
  groupId: string;
  campaignId: string;
}) {
  const isGroup = !!groupId;
  const { profile } = useUserProfileStore();
  const [tabValue, setTabValue] = useState<number>(0);
  const [notifyMe, setNotifyMe] = useState<boolean>(profile.notifyOnTextReply ?? false);
  const editorRef = useRef<TinyMCETypes.Editor | null>(null);
  const { handleChangeContainerType } = useMessageDialogHandlers();

  const messageState = useMessageInputState();
  const { handleClearInputState } = useMessageInputHandlers();
  const isEmail = messageState.messageType === "email";

  const messageScheduleState = useMessageScheduleState();
  const { handleClearScheduleOptions, handleCalendarChange } = useMessageScheduleHandlers();
  const { handleClearTemplateInputState } = useMessageTemplateHandlers();

  const tabChange = (_: React.SyntheticEvent, newValue: number) => {
    const container = GetContainerTypeByTabNumber(newValue, isGroup);
    const hasToggledBetweenEmailAndText = getMessageType(container) !== messageState.messageType;
    handleChangeContainerType(container);
    setTabValue(newValue);

    if (hasToggledBetweenEmailAndText) {
      handleClearScheduleOptions();
      handleClearInputState();
      handleClearTemplateInputState();
      const isEmailTab = newValue === 1;
      setNotifyMe(isEmailTab ? (profile.notifyOnEmailReply ?? false) : (profile.notifyOnTextReply ?? false));
    } else {
      const newTime = messageScheduleState.scheduleDateTime
        ? dayjs(messageScheduleState.scheduleDateTime)
        : toNearestFiveMinutes();
      handleCalendarChange(newTime);
    }
  };

  const messageCandidates = useAppSelector(selectSelectedCandidates);

  const sendMessageBody: IMessageSendPost = useMemo(
    () => ({
      candidates: messageCandidates.map((c) => c.candidateId),
      isEmail,
      isText: !isEmail,
      isGroup,
      groupId,
      campaignId,
      messages: messageScheduleState.msgListQueue,
      uiComponent: "overrides in messagequeries",
      userId: "overrides in messagequeries",
      userName: "",
      replyMessageId: "",
      notify: notifyMe,
      notifyEmail: "overrides in messagequeries",
    }),
    [campaignId, groupId, isEmail, messageScheduleState.msgListQueue, messageCandidates, notifyMe],
  );

  const messageMutationState = useMessageMutationState(sendMessageBody);

  return (
    <Box>
      <Box
        sx={(theme) => ({
          borderBottom: 1,
          borderColor: theme.palette.divider,
        })}
      >
        <Tabs
          data-test="message-delivery-tabs"
          value={tabValue}
          onChange={tabChange}
          aria-label="Message Delivery Tabs"
        >
          <Tab
            icon={<Textsms />}
            iconPosition="start"
            sx={{ minHeight: 0 }}
            label={
              <Badge
                data-test="message-delivery-tab-text-now"
                badgeContent={0}
                color="primary"
                showZero={false}
              >
                {groupId ? "Text Group" : "Text Candidates"}
              </Badge>
            }
            {...historyListProps(0)}
          />
          <Tab
            icon={<Email />}
            iconPosition="start"
            sx={{ minHeight: 0 }}
            label={
              <Badge
                data-test="message-delivery-tab-email-now"
                badgeContent={0}
                color="primary"
                showZero={false}
              >
                {groupId ? "Email Group" : "Email Candidates"}
              </Badge>
            }
            {...historyListProps(1)}
          />
        </Tabs>
      </Box>
      <Box>
        <MemoTabPanel value={tabValue} index={0}>
          <TextMessageSendOrSchedule
            messageMutationState={messageMutationState}
            isGroup={!!groupId}
            notifyMe={notifyMe}
            setNotifyMe={setNotifyMe}
          />
        </MemoTabPanel>
        <MemoTabPanel value={tabValue} index={1}>
          <EmailMessageSendOrSchedule
            editorRef={editorRef}
            messageMutationState={messageMutationState}
            isGroup={!!groupId}
            notifyMe={notifyMe}
            setNotifyMe={setNotifyMe}
          />
        </MemoTabPanel>
      </Box>
    </Box>
  );
}

export const MemoMessageDeliveryTabs = memo(MessageDeliveryTabs);
